/**
 * Keeps the buy button locked until the pre-order terms are ticked.
 *
 * The checkbox is bound to the product form by the form attribute, so the
 * consent submits as a line item property without living inside the form.
 *
 * The lock leaves the button's `disabled` to the theme. Toggling it ourselves
 * lost the lock whenever the theme updated the button in place - a size change
 * in the quick-add popup switched it back on - and could re-enable a sold-out
 * size when the box was ticked. Instead the button is marked aria-disabled (the
 * theme draws that as disabled), and the click and the submit are stopped on
 * window, ahead of the theme's own handlers, which listen on document.
 */

/** @type {Set<PreorderTerms>} */
const gates = new Set();

class PreorderTerms extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('.preorder-terms__checkbox');
    if (!this.checkbox) return;
    if (this.dataset.gate !== 'true') return;

    this.sync = this.sync.bind(this);
    this.checkbox.addEventListener('change', this.sync);
    gates.add(this);

    // the button is re-rendered or patched on variant changes, so keep watching
    this.observer = new MutationObserver(this.sync);
    this.watchForm();

    // The quick-add popup inserts this block ahead of its form, so the form may
    // not be in the document yet when the block connects: bind again once the
    // insertion has finished.
    requestAnimationFrame(() => {
      if (this.isConnected) this.watchForm();
    });
  }

  disconnectedCallback() {
    gates.delete(this);
    if (this.observer) this.observer.disconnect();
    this.watchedForm = null;
    if (this.checkbox) this.checkbox.removeEventListener('change', this.sync);
  }

  get form() {
    return document.getElementById(this.dataset.form);
  }

  get locked() {
    return !this.checkbox.checked;
  }

  watchForm() {
    const form = this.form;
    if (form && form !== this.watchedForm) {
      this.observer.disconnect();
      this.observer.observe(form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-disabled'],
      });
      this.watchedForm = form;
    }

    this.sync();
  }

  buttons() {
    const form = this.form;
    if (!form) return [];
    return [...form.querySelectorAll('.add-to-cart-button, [name="add"]')];
  }

  sync() {
    for (const button of this.buttons()) {
      if (this.locked) {
        if (button.getAttribute('aria-disabled') !== 'true') {
          button.setAttribute('aria-disabled', 'true');
          button.dataset.preorderLocked = 'true';
        }
      } else if (button.dataset.preorderLocked === 'true') {
        button.removeAttribute('aria-disabled');
        delete button.dataset.preorderLocked;
      }
    }
  }

  /**
   * Whether this gate holds the given element: its form, or anything submitting it.
   * @param {Element} element
   */
  guards(element) {
    const form = this.form;
    if (!form) return false;
    if (element === form) return true;

    const button = element.closest('.add-to-cart-button, [name="add"]');
    return Boolean(button && form.contains(button));
  }
}

/**
 * Stops the add while a gate on that form is locked. Capture on window runs
 * before the theme's capture listeners on document; the click is stopped too,
 * so the add-to-cart animation does not play for an add that never happens,
 * and the sticky bar, which clicks the main button, is held as well.
 * @param {Event} event
 */
function holdLockedAdd(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  for (const gate of gates) {
    if (gate.locked && gate.guards(target)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      gate.checkbox.focus();
      return;
    }
  }
}

window.addEventListener('click', holdLockedAdd, { capture: true });
window.addEventListener('submit', holdLockedAdd, { capture: true });

if (!customElements.get('preorder-terms')) {
  customElements.define('preorder-terms', PreorderTerms);
}
